import { PERMISSION_PRESET } from "@/core/config/constant";

export const GLOBAL_MENU = {
  FORM: "form",
  ANSWER: "answer",
  ACCOUNT: "account",
  APPROVAL: "approval",
  ROLE: "role",
  TENANT: "tenant",
  MAIL: "mail",
  HISTORY: "history",
};

const ALL_MENUS = Object.values(GLOBAL_MENU);

function isEnabledFlag(value) {
  return value === true || value === 1 || value === "1";
}

function getRoleFlag(currentUser, flag) {
  const role = currentUser.role || {};
  const value = role[flag] !== undefined ? role[flag] : currentUser[flag];
  return isEnabledFlag(value);
}

function buildAdminMenuAccess() {
  const menus = ALL_MENUS.reduce((acc, key) => {
    acc[key] = key !== GLOBAL_MENU.HISTORY;
    return acc;
  }, {});

  return {
    menus,
    showTenantSettings: true,
    showTenantSearch: true,
  };
}

function buildEmptyMenus() {
  return ALL_MENUS.reduce((acc, key) => {
    acc[key] = false;
    return acc;
  }, {});
}

function resolveCustomMenuAccess(currentUser = {}) {
  const menus = buildEmptyMenus();
  let showTenantSettings = false;
  let showTenantSearch = false;

  if (getRoleFlag(currentUser, "kengen_flg")) {
    menus[GLOBAL_MENU.ACCOUNT] = true;
    menus[GLOBAL_MENU.APPROVAL] = true;
    menus[GLOBAL_MENU.ROLE] = true;
    menus[GLOBAL_MENU.TENANT] = true;
    showTenantSearch = true;
  }

  if (getRoleFlag(currentUser, "form_flg")) {
    menus[GLOBAL_MENU.FORM] = true;
  }

  if (getRoleFlag(currentUser, "answer_flg")) {
    menus[GLOBAL_MENU.ANSWER] = true;
  }

  if (getRoleFlag(currentUser, "tennant_flg")) {
    menus[GLOBAL_MENU.TENANT] = true;
    showTenantSettings = true;
    showTenantSearch = true;
  }

  if (getRoleFlag(currentUser, "mail_flg")) {
    menus[GLOBAL_MENU.MAIL] = true;
  }

  return {
    menus,
    showTenantSettings,
    showTenantSearch,
  };
}

function resolvePermissionPresetAccess(currentUser = {}) {
  if (!currentUser || !Object.keys(currentUser).length) {
    return {
      menus: buildEmptyMenus(),
      showTenantSettings: false,
      showTenantSearch: false,
    };
  }

  const role = currentUser.role || {};
  const preset = Number(
    currentUser.permission_preset ?? role.permission_preset
  );

  switch (preset) {
    case PERMISSION_PRESET.FORM_ANSWERER: {
      const menus = buildEmptyMenus();
      menus[GLOBAL_MENU.ANSWER] = true;
      return {
        menus,
        showTenantSettings: false,
        showTenantSearch: false,
      };
    }
    case PERMISSION_PRESET.FORM_EDITOR: {
      const menus = buildEmptyMenus();
      menus[GLOBAL_MENU.FORM] = true;
      menus[GLOBAL_MENU.ANSWER] = true;
      return {
        menus,
        showTenantSettings: false,
        showTenantSearch: false,
      };
    }
    case PERMISSION_PRESET.ADMIN:
      return buildAdminMenuAccess();
    case PERMISSION_PRESET.CUSTOM:
      return resolveCustomMenuAccess(currentUser);
    default:
      return {
        menus: buildEmptyMenus(),
        showTenantSettings: false,
        showTenantSearch: false,
      };
  }
}

export function resolveGlobalMenuAccess(currentUser = {}) {
  return resolvePermissionPresetAccess(currentUser);
}

export function canShowGlobalMenu(currentUser, menu) {
  return resolveGlobalMenuAccess(currentUser).menus[menu] === true;
}

const PUBLIC_ROUTE_NAMES = new Set([
  "login",
  "register",
  "tenant_login",
  "forgot-password",
  "forgot-password-tenant_dir",
  "reset-password",
  "tenant_reset_password",
  "404",
  "403",
  "permission-denied",
]);

const ROUTE_MENU_RULES = {
  "form-base-list": { menu: GLOBAL_MENU.FORM },
  "form-base-add": { menu: GLOBAL_MENU.FORM },
  "form-item-add": { menu: GLOBAL_MENU.FORM },
  "form-item-add-loading": { menu: GLOBAL_MENU.FORM },
  "edit-form": { menu: GLOBAL_MENU.FORM },
  "form-preview": { menu: GLOBAL_MENU.FORM },
  "form-answer-list": { menu: GLOBAL_MENU.ANSWER },
  "form-base-detail": { menu: GLOBAL_MENU.ANSWER },
  "register-account": { menu: GLOBAL_MENU.ACCOUNT },
  "list-account": { menu: GLOBAL_MENU.ACCOUNT },
  "edit-account": { menu: GLOBAL_MENU.ACCOUNT },
  "process-approval": { menu: GLOBAL_MENU.APPROVAL },
  "process-approval-edit": { menu: GLOBAL_MENU.APPROVAL },
  "list-approval": { menu: GLOBAL_MENU.APPROVAL },
  "add-role": { menu: GLOBAL_MENU.ROLE },
  "list-role": { menu: GLOBAL_MENU.ROLE },
  "edit-role": { menu: GLOBAL_MENU.ROLE },
  "form-tenant-add": { menu: GLOBAL_MENU.TENANT, tenantSetting: true },
  "edit-tenant": { menu: GLOBAL_MENU.TENANT, tenantSetting: true },
  "tenant-list": { menu: GLOBAL_MENU.TENANT, tenantSearch: true },
  template: { menu: GLOBAL_MENU.MAIL },
  history: { menu: GLOBAL_MENU.HISTORY },
};

export function isPublicRoute(routeName) {
  return !routeName || PUBLIC_ROUTE_NAMES.has(routeName);
}

export function isHiddenRoute(routeName) {
  const rule = ROUTE_MENU_RULES[routeName];
  return Boolean(rule) && rule.menu === GLOBAL_MENU.HISTORY;
}

export function canAccessRoute(routeName, currentUser = {}) {
  if (isPublicRoute(routeName)) {
    return true;
  }

  const rule = ROUTE_MENU_RULES[routeName];
  if (!rule) {
    return true;
  }

  if (rule.menu === GLOBAL_MENU.HISTORY) {
    return false;
  }

  const access = resolvePermissionPresetAccess(currentUser);
  if (!access.menus[rule.menu]) {
    return false;
  }

  if (rule.tenantSetting && !access.showTenantSettings) {
    return false;
  }

  if (rule.tenantSearch && !access.showTenantSearch) {
    return false;
  }

  return true;
}
